import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { FiLock, FiEye, FiEyeOff, FiArrowLeft, FiMail } from 'react-icons/fi';
import './UpdatePassword.css';

const UpdatePassword = () => {
  const navigate = useNavigate();
  const { changePassword, forgotPassword, partner } = useAuth();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  const validatePassword = (password) => {
    const minLength = 6;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return 'Password must be at least 6 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate new password
      const passwordError = validatePassword(formData.newPassword);
      if (passwordError) {
        toast.error(passwordError);
        return;
      }

      // Check if passwords match
      if (formData.newPassword !== formData.confirmPassword) {
        toast.error('New passwords do not match');
        return;
      }

      // Check if new password is different from current
      if (formData.currentPassword === formData.newPassword) {
        toast.error('New password must be different from current password');
        return;
      }

      const result = await changePassword(formData.currentPassword, formData.newPassword);
      
      if (result.success) {
        toast.success('Password updated successfully!');
        navigate('/dashboard');
      } else {
        toast.error(result.error || 'Failed to update password');
      }
    } catch (error) {
      toast.error('An error occurred while updating password');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);

    try {
      const email = forgotPasswordEmail || partner?.email;
      if (!email) {
        toast.error('Email is required');
        return;
      }

      const result = await forgotPassword(email);
      
      if (result.success) {
        toast.success(result.message || 'Password reset email sent! Please check your inbox.');
        setShowForgotPassword(false);
        setForgotPasswordEmail('');
      } else {
        toast.error(result.error || 'Failed to send password reset email');
      }
    } catch (error) {
      toast.error('An error occurred while sending password reset email');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="update-password-container">
      <div className="page-header">
        <div className="page-title">
          <h2>Update Password</h2>
          <p>Change your account password securely</p>
        </div>
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          <FiArrowLeft />
          Back to Dashboard
        </button>
      </div>

      {showForgotPassword ? (
        <div className="update-password-content">
          <div className="forgot-password-section">
            <h3>Request Password Reset</h3>
            <p>Enter your email address and we'll send you a link to reset your password.</p>
            <form onSubmit={handleForgotPassword} className="password-form">
              <div className="form-group">
                <label htmlFor="forgotPasswordEmail">Email Address</label>
                <div className="input-wrapper">
                  <FiMail className="input-icon" />
                  <input
                    type="email"
                    id="forgotPasswordEmail"
                    name="forgotPasswordEmail"
                    value={forgotPasswordEmail || partner?.email || ''}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  className="update-button"
                >
                  {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </div>

              <div className="form-group">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail('');
                  }}
                  className="back-btn"
                  style={{ width: '100%' }}
                >
                  Back to Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <div className="update-password-content">
          <form onSubmit={handleSubmit} className="password-form">
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <div className="input-wrapper">
                <FiLock className="input-icon" />
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  placeholder="Enter your current password"
                  required
                  className="form-input"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('current')}
                >
                  {showPasswords.current ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              <div className="forgot-password-link">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="forgot-password-btn"
                >
                  Forgot your password?
                </button>
              </div>
            </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="input-wrapper">
              <FiLock className="input-icon" />
              <input
                type={showPasswords.new ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter your new password"
                required
                className="form-input"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('new')}
              >
                {showPasswords.new ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            <div className="password-requirements">
              <p>Password must contain:</p>
              <ul>
                <li className={formData.newPassword.length >= 6 ? 'valid' : 'invalid'}>
                  At least 6 characters
                </li>
                <li className={/[A-Z]/.test(formData.newPassword) ? 'valid' : 'invalid'}>
                  One uppercase letter
                </li>
                <li className={/[a-z]/.test(formData.newPassword) ? 'valid' : 'invalid'}>
                  One lowercase letter
                </li>
                <li className={/\d/.test(formData.newPassword) ? 'valid' : 'invalid'}>
                  One number
                </li>
                <li className={/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword) ? 'valid' : 'invalid'}>
                  One special character
                </li>
              </ul>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="input-wrapper">
              <FiLock className="input-icon" />
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your new password"
                required
                className="form-input"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('confirm')}
              >
                {showPasswords.confirm ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {formData.confirmPassword && (
              <div className={`password-match ${formData.newPassword === formData.confirmPassword ? 'valid' : 'invalid'}`}>
                {formData.newPassword === formData.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
              </div>
            )}
          </div>

          <div className="form-group">
            <button
              type="submit"
              disabled={loading}
              className="update-button"
            >
              {loading ? 'Updating Password...' : 'Update Password'}
            </button>
          </div>
        </form>
        </div>
      )}
    </div>
  );
};

export default UpdatePassword; 